import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input} from '@angular/core';
import {DetectMethodChanges} from '../../utils';
import {
    ApgGridOrientation,
    ApgHidableSection,
    ApgSectionVisibility,
    ApgSectionVisibilityService,
    makeDefaultSectionVisibility
} from '../services/apg-section-visibility.service';

interface SectionRow {
    section: ApgHidableSection;
    label: string;
}

// The "Sections" menu behind the burger in the PORTFOLIOS header: one row per hidable section with an
// on/off switch. A flip is applied and persisted at once — there is no Save step by design.
@Component({
    selector: 'ets-apg-section-visibility-menu',
    templateUrl: 'apg-section-visibility-menu.component.html',
    styleUrls: ['apg-section-visibility-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApgSectionVisibilityMenuComponent {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _sectionVisibilityService: ApgSectionVisibilityService
    ) {
    }

    @Input() orientation: ApgGridOrientation;

    // Open question 2 (default taken): a section the backend has not granted is not listed.
    @Input() isZonesGridAvailable = false;

    visible = false;

    visibility: ApgSectionVisibility = makeDefaultSectionVisibility();

    private readonly _allRows: SectionRow[] = [
        {section: 'settings', label: 'Settings'},
        {section: 'strategies', label: 'Strategies'},
        {section: 'pricebox', label: 'Price Box'},
        {section: 'zones-grid', label: 'Zones Grid'}
    ];

    get rows(): SectionRow[] {
        return this._allRows.filter(r => r.section !== 'zones-grid' || this.isZonesGridAvailable);
    }

    get title(): string {
        if (this.orientation === 'left') {
            return 'Sections (Left)';
        }
        if (this.orientation === 'right') {
            return 'Sections (Right)';
        }
        return 'Sections';
    }

    @DetectMethodChanges()
    show(): void {
        this.visibility = this._sectionVisibilityService.getVisibility(this.orientation);
        this.visible = true;
    }

    @DetectMethodChanges()
    onHidden(): void {
        this.visible = false;
    }

    @DetectMethodChanges()
    close(): void {
        this.visible = false;
    }

    // dx-switch fires onValueChanged on programmatic sets too; only a user flip is a save.
    @DetectMethodChanges()
    onSwitchChanged(section: ApgHidableSection, ev: any): void {
        if (!ev || isVoidEvent(ev)) {
            return;
        }
        const value = !!ev.value;
        if (this.visibility[section] === value) {
            return;
        }
        this.visibility = {...this.visibility, [section]: value};
        this._sectionVisibilityService.saveVisibility(this.orientation, this.visibility);
    }
}

function isVoidEvent(ev: any): boolean {
    return !ev.event;
}
